import type { ConnectionStatusHistoryEntry, RendererDiagnosticContext } from '@m43/bridge-interface';
import type { ConnectionStatusContextValue } from '../connection-status/connection-status-context';

const pageLoadTimestamp = Date.now();

type PerformanceMemory = {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
};

function getPerformanceMemory(): PerformanceMemory | null {
    // Chrome-only API — not available in all browsers
    const perf = performance as { memory?: PerformanceMemory };
    return perf.memory ?? null;
}

function countWebGLContexts(): { active: number; total: number } {
    const canvases = Array.from(document.querySelectorAll('canvas'));
    let active = 0;
    for (const canvas of canvases) {
        try {
            const gl =
                canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) ??
                canvas.getContext('webgl', { failIfMajorPerformanceCaveat: false });
            if (gl && !gl.isContextLost()) {
                active++;
            }
        } catch {
            // Skip canvases that throw (e.g. 2d-only canvases)
        }
    }
    return { active, total: canvases.length };
}

export function getRendererDiagnosticContext(
    connectionStatus: ConnectionStatusContextValue,
    connectionStatusHistory: ConnectionStatusHistoryEntry[]
): RendererDiagnosticContext {
    const memory = getPerformanceMemory();
    const bytesToMb = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 100) / 100;
    const webglCounts = countWebGLContexts();

    const result: RendererDiagnosticContext = {
        sessionDurationSeconds: Math.round((Date.now() - pageLoadTimestamp) / 1000),
        hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
        activeWebGLContextCount: webglCounts.active,
        totalCanvasCount: webglCounts.total,
        connectionStatus: { ...connectionStatus },
        connectionStatusHistory: [...connectionStatusHistory],
    };

    if (memory) {
        result.jsHeapUsedMb = bytesToMb(memory.usedJSHeapSize);
        result.jsHeapTotalMb = bytesToMb(memory.totalJSHeapSize);
        result.jsHeapLimitMb = bytesToMb(memory.jsHeapSizeLimit);
    }

    const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory;
    if (deviceMemory != null) {
        result.deviceMemoryGb = deviceMemory;
    }

    return result;
}
